import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  useGetMultiplacerMigrationRunsQuery,
  usePostMultiplacerMigrationRunsMutation,
} from "../../openapi";
import { FailedAlert } from "../components/FailedAlert";

export function MigrationsAdminPage() {
  const { data, error, isLoading, refetch } = useGetMultiplacerMigrationRunsQuery(undefined, {
    pollingInterval: 5000,
  });
  const [run, { isLoading: running, error: runError }] = usePostMultiplacerMigrationRunsMutation();
  const [selected, setSelected] = useState<{
    id: string;
    description: string;
    databaseName: string;
    hosts: string;
    rerun: boolean;
  } | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const [allowRerun, setAllowRerun] = useState(false);
  const [details, setDetails] = useState<string | null>(null);
  async function execute() {
    if (!selected) return;
    try {
      await run({
        body: {
          migrationId: selected.id,
          confirmDatabaseName: confirmation,
          confirmHosts: selected.hosts,
          allowRerun,
        },
      }).unwrap();
      setSelected(null);
    } catch {
      /* The mutation error is displayed below; history is refreshed even after failure. */
    } finally {
      void refetch();
    }
  }
  return (
    <ScrollArea h="100%" w="100%">
      <Stack p="md">
        <Group justify="space-between">
          <Title order={2}>Migreringer</Title>
          <Button variant="neutral" onClick={() => void refetch()}>
            Oppdater
          </Button>
        </Group>
        {isLoading && <Loader />}
        {!!error && (
          <FailedAlert
            error={error}
            message="Kunne ikke hente migreringer. Tilgang krever utviklerrettigheter."
          />
        )}
        {!!runError && !selected && (
          <FailedAlert error={runError} message="Migreringen kunne ikke fullføres." />
        )}
        {data && (
          <>
            <Alert title="Tilkoblet database">
              <Text>{data.target.hosts}</Text>
              <Code>{data.target.databaseName}</Code>
              <Text size="sm">
                Migreringer endrer denne databasen, også når appen kjøres lokalt.
              </Text>
            </Alert>
            {data.locked && (
              <Alert color="yellow">
                En migrering kjører eller har blitt avbrutt. En gjenværende lås må kontrolleres før
                ny kjøring.
              </Alert>
            )}
            {data.migrations.map((migration) => (
              <Card key={migration.id} withBorder>
                <Stack>
                  <Group justify="space-between">
                    <Text fw={600}>{migration.description}</Text>
                    <Badge>{migration.latestRun?.status ?? "Ikke kjørt"}</Badge>
                  </Group>
                  <Code>{migration.id}</Code>
                  {migration.latestRun && (
                    <Text size="sm">
                      {migration.latestRun.startedAt} · {migration.latestRun.startedBy}
                    </Text>
                  )}
                  <Group>
                    <Button
                      disabled={running || data.locked}
                      onClick={() => {
                        setSelected({
                          ...data.target,
                          id: migration.id,
                          description: migration.description,
                          rerun: migration.latestRun?.status === "success",
                        });
                        setConfirmation("");
                        setAllowRerun(false);
                      }}
                    >
                      Kjør migrering
                    </Button>
                    {migration.latestRun && (
                      <Button
                        variant="neutral"
                        onClick={() => setDetails(details === migration.id ? null : migration.id)}
                      >
                        Vis siste kjøring
                      </Button>
                    )}
                  </Group>
                  {details === migration.id && migration.latestRun && (
                    <>
                      <Text size="sm">Kjøring: {migration.latestRun._id}</Text>
                      {migration.latestRun.finishedAt && (
                        <Text size="sm">Avsluttet: {migration.latestRun.finishedAt}</Text>
                      )}
                      {migration.latestRun.error && (
                        <Alert color="red">{migration.latestRun.error}</Alert>
                      )}
                      <Code block>
                        {migration.latestRun.logs.join("\n") || "Ingen logger ennå."}
                      </Code>
                    </>
                  )}
                </Stack>
              </Card>
            ))}
          </>
        )}
        <Modal
          opened={selected !== null}
          onClose={() => {
            if (!running) setSelected(null);
          }}
          title="Bekreft migrering"
          closeOnClickOutside={!running}
          closeOnEscape={!running}
          withCloseButton={!running}
        >
          {selected && (
            <Stack>
              <Text>{selected.description}</Text>
              <Text size="sm">{selected.hosts}</Text>
              <TextInput
                label={`Skriv databasenavnet «${selected.databaseName}» for å bekrefte`}
                value={confirmation}
                onChange={(event) => setConfirmation(event.currentTarget.value)}
                disabled={running}
                autoComplete="off"
              />
              <Checkbox
                label="Tillat ny kjøring hvis migreringen allerede er fullført"
                checked={allowRerun}
                onChange={(event) => setAllowRerun(event.currentTarget.checked)}
                disabled={running}
              />
              {!!runError && (
                <FailedAlert error={runError} message="Migreringen kunne ikke fullføres." />
              )}
              <Group justify="flex-end">
                <Button variant="neutral" disabled={running} onClick={() => setSelected(null)}>
                  Avbryt
                </Button>
                <Button
                  loading={running}
                  disabled={
                    confirmation !== selected.databaseName ||
                    (selected.rerun && !allowRerun) ||
                    data?.locked
                  }
                  onClick={() => void execute()}
                >
                  Kjør migrering
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Stack>
    </ScrollArea>
  );
}
